/**
 * По улике со страницы — закрытый вердикт шкафа. Не вкусовщина.
 *
 * Это факт сверки, не метка оператора: `sightings.verdict` отсюда не ставим.
 * Пустые страницы — `unmatched`, не ор. Уехавший кэф — `drift`. Нет имён —
 * другой матч. Исход длинный и его нет — другой рынок или сторона.
 */

import type { PageClip } from './clip';

export const LIE_CODES = [
  'ok',
  'stale',
  'drift',
  'wrong_market',
  'wrong_side',
  'wrong_event',
  'dead',
  'thin',
  'bad_link',
  'cost',
  'unmatched',
] as const;

export type LieCode = (typeof LIE_CODES)[number];

export type PageLie = {
  verdict: LieCode;
  why: string;
};

const MARKETISH = /тотал|фора|больше|меньше|total|spread|over|under|handicap/i;

export function lieOf(clips: readonly PageClip[]): PageLie {
  if (clips.length === 0) return { verdict: 'unmatched', why: 'страниц нет' };
  const readable = clips.filter((one) => one.state === 'match' || one.state === 'moved');
  if (readable.length === 0) {
    return { verdict: 'unmatched', why: 'страницы не прочитались' };
  }
  const drifted = readable.filter((one) => one.state === 'moved');
  if (drifted.length > 0) {
    const one = drifted[0]!;
    return {
      verdict: 'drift',
      why: `${one.venue}: лента ${one.ours}, страница ${one.closest ?? '—'}`,
    };
  }
  const named = readable.filter((one) => one.who.length > 0);
  if (named.length > 0 && named.every((one) => !one.whoHit)) {
    return { verdict: 'wrong_event', why: 'участников с ленты на странице нет' };
  }
  const sideMiss = readable.filter(
    (one) => one.side.trim().length >= 4 && one.whoHit && !one.sideHit,
  );
  if (sideMiss.length > 0) {
    const one = sideMiss[0]!;
    const verdict: LieCode = MARKETISH.test(one.side) ? 'wrong_market' : 'wrong_side';
    return { verdict, why: `${one.venue}: исход «${one.side}» на странице не найден` };
  }
  return { verdict: 'ok', why: 'кэф и имена на страницах есть' };
}

export function parseLie(raw: unknown): PageLie | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const verdict = (raw as { verdict?: unknown }).verdict;
  const why = (raw as { why?: unknown }).why;
  if (typeof verdict !== 'string' || !(LIE_CODES as readonly string[]).includes(verdict)) {
    return null;
  }
  if (typeof why !== 'string') return null;
  return { verdict: verdict as LieCode, why: why.slice(0, 240) };
}
